import { getAllPassengers } from '@backoffice-frontend/ad-passenger-list'
import {
  FccPassengerAuthenticationState,
  FccServiceCancellationTypeEnum,
} from '@backoffice-frontend/graphql'
import { MoiaGrid } from '@backoffice-frontend/patterns'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import {
  Button,
  Heading,
  LoadingIndicator,
  Radio,
  Text,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type ServiceCancellationDecision,
  ServiceCancellationInitiatedAction,
} from '../../../ServiceCancellation/ServiceCancellationProvider'
import { useServiceCancellation } from '../../../ServiceCancellation/useServiceCancellation'
import type { AdVehicleStreamVehicleFragment } from '../../AdVehicleStream.hook'
import { ActionCard } from '../ActionCard'

export const EmergencyStopActionCard = ({
  vehicle,
}: {
  vehicle: AdVehicleStreamVehicleFragment
}) => {
  const [selectedDecision, setSelectedDecision] =
    useState<ServiceCancellationDecision | null>(null)

  const { state, initiate, initiatedAction } = useServiceCancellation()

  const { t } = useTranslation()

  const hasPermissionToManageTrips = useUserHasAccess({
    requiredRights: [BoumRight.FLEET_CONTROL_CANCEL_TRIP],
  })

  const passengers = getAllPassengers(vehicle.fccUpcomingWaypoints ?? [])
  const tripManagementAllowed =
    hasPermissionToManageTrips &&
    passengers.some(
      passenger =>
        (passenger.pickedUpAt !== null &&
          passenger.nextWaypointActionType !== 'PICKUP') ||
        passenger.authenticationState ===
          FccPassengerAuthenticationState.Upcoming,
    ) &&
    state.matches('idle')

  return (
    <ActionCard headline={t('adFleetMap_incident_assessSituation_button')}>
      <MoiaGrid
        alignContent="start"
        css={theme => ({
          padding: theme.space.Space4,
          height: '100%',
          gap: theme.space.Space4,
        })}
      >
        {/* Continue service option */}
        <MoiaGrid>
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
              background: theme.semantic.ColorAccessoryReducedAlternative,
              border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              padding: theme.space.Space2,
              borderRadius: 24,
              '& div > label > div': {
                zoom: 1.75,
              },
            })}
          >
            <Radio
              label=""
              value={'continueAllTrips'}
              checked={selectedDecision === 'continueAllTrips'}
              onChange={() => setSelectedDecision('continueAllTrips')}
              id="continueService"
              disabled={!state.matches('idle')}
            />
            <label htmlFor="continueService">
              <Heading size="lg">
                {t('adFleetMap_continueService_label')}
              </Heading>
            </label>
          </div>

          <Text
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              fontSize: 12,
            })}
            color="reduced"
          >
            {t('adFleetMap_continueServiceHelper_caption')}
          </Text>
          <Button
            label={t('adFleetMap_continueService_button')}
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              width: 'fit-content',
            })}
            disabled={
              selectedDecision !== 'continueAllTrips' ||
              (!state.matches('idle') &&
                initiatedAction ===
                  ServiceCancellationInitiatedAction.ContinueService)
            }
            onClick={() =>
              initiate(
                vehicle.id,
                FccServiceCancellationTypeEnum.AllTrips,
                ServiceCancellationInitiatedAction.ContinueService,
              )
            }
            icon={
              !state.matches('idle') &&
              initiatedAction ===
                ServiceCancellationInitiatedAction.ContinueService ? (
                <LoadingIndicator />
              ) : undefined
            }
          />
        </MoiaGrid>

        {/* Manage trips option */}
        <MoiaGrid>
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
              background: theme.semantic.ColorAccessoryReducedAlternative,
              border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              padding: theme.space.Space2,
              borderRadius: 24,
              '& div > label > div': {
                zoom: 1.75,
              },
            })}
          >
            <Radio
              label=""
              value={'cancelSomeTrips'}
              checked={selectedDecision === 'cancelSomeTrips'}
              onChange={() => setSelectedDecision('cancelSomeTrips')}
              id="manageTrips"
              disabled={!tripManagementAllowed}
            />
            <label htmlFor="manageTrips">
              <Heading size="lg">{t('adFleetMap_manageTrips_label')}</Heading>
            </label>
          </div>

          <Text
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              fontSize: 12,
            })}
            color="reduced"
          >
            {t('adFleetMap_manageTripsHelper_caption')}
          </Text>
          <Button
            label={t('adFleetMap_selectTrips_button')}
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              width: 'fit-content',
            })}
            disabled={
              selectedDecision !== 'cancelSomeTrips' ||
              !tripManagementAllowed ||
              (!state.matches('idle') &&
                initiatedAction ===
                  ServiceCancellationInitiatedAction.TripManagement)
            }
            onClick={() => {
              initiate(
                vehicle.id,
                FccServiceCancellationTypeEnum.SomeTrips,
                ServiceCancellationInitiatedAction.TripManagement,
              )
            }}
            icon={
              !state.matches('idle') &&
              initiatedAction ===
                ServiceCancellationInitiatedAction.TripManagement ? (
                <LoadingIndicator />
              ) : undefined
            }
          />
        </MoiaGrid>

        {/* Cancel service option */}
        <MoiaGrid>
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
              background: theme.semantic.ColorAccessoryReducedAlternative,
              border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              padding: theme.space.Space2,
              borderRadius: 24,
              '& div > label > div': {
                zoom: 1.75,
              },
            })}
          >
            <Radio
              label=""
              value={'cancelAllTrips'}
              checked={selectedDecision === 'cancelAllTrips'}
              onChange={() => setSelectedDecision('cancelAllTrips')}
              id="cancelAllTrips"
              disabled={!state.matches('idle')}
            />
            <label htmlFor="cancelAllTrips">
              <Heading size="lg">{t('adFleetMap_cancelService_label')}</Heading>
            </label>
          </div>

          <Text
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              fontSize: 12,
            })}
            color="reduced"
          >
            {t('adFleetMap_cancelServiceHelper_caption')}
          </Text>
          <Button
            variant="critical"
            onClick={() =>
              initiate(
                vehicle.id,
                FccServiceCancellationTypeEnum.AllTrips,
                ServiceCancellationInitiatedAction.CancelService,
              )
            }
            label={t('common_cancelService_button')}
            disabled={
              selectedDecision !== 'cancelAllTrips' ||
              (!state.matches('idle') &&
                initiatedAction ===
                  ServiceCancellationInitiatedAction.CancelService)
            }
            css={theme => ({
              marginLeft: theme.spacing(7.5),
              width: 'fit-content',
            })}
            icon={
              !state.matches('idle') &&
              initiatedAction ===
                ServiceCancellationInitiatedAction.CancelService ? (
                <LoadingIndicator />
              ) : undefined
            }
          />
        </MoiaGrid>
      </MoiaGrid>
    </ActionCard>
  )
}
